/**
 * 跑腿首页
 * 地图只服务"起点选点"，不再渲染附近骑手 marker。
 */
"use client";

import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { ChevronDown, Megaphone, MapPin, Clock } from "lucide-react";
import { Card } from "@/components/ui/card";
import { AMap } from "@/components/ui/map";
import { loadAMap, ensureAMapPlugin, amapKeyMissing } from "@/lib/amap-loader";
import { useAppStore } from "@/lib/store";
import { zoneList } from "@/lib/api/market";
import { cn } from "@/lib/utils";
import { pickMapCenter } from "@/lib/geo";

type ModuleType = "SEND" | "TAKE" | "BUY" | "1V1" | "WAN" | "RIDE";

type RunModule = {
  id: number;
  name: string;
  type: ModuleType;
  tag?: string;
  dest_tips: string;
  types: { id: number; name: string; image: string }[];
};

const MOCK_MODULES: RunModule[] = [
  {
    id: 1,
    name: "帮我送",
    type: "SEND",
    tag: "热门",
    dest_tips: "请输入送货地址",
    types: [
      { id: 11, name: "文件", image: "/static/index/coupon-logo.png" },
      { id: 12, name: "鲜花", image: "/static/index/coupon-logo.png" },
      { id: 13, name: "蛋糕", image: "/static/index/coupon-logo.png" },
      { id: 14, name: "钥匙", image: "/static/index/coupon-logo.png" },
    ],
  },
  {
    id: 2,
    name: "帮我取",
    type: "TAKE",
    dest_tips: "请输入取件地址",
    types: [],
  },
  {
    id: 3,
    name: "帮我买",
    type: "BUY",
    tag: "推荐",
    dest_tips: "请输入收货地址",
    types: [],
  },
  {
    id: 4,
    name: "1V1",
    type: "1V1",
    dest_tips: "请输入送达地址",
    types: [],
  },
  {
    id: 5,
    name: "万能服务",
    type: "WAN",
    dest_tips: "请输入送达地址",
    types: [],
  },
  {
    id: 6,
    name: "送人",
    type: "RIDE",
    tag: "新",
    dest_tips: "请输入到达地址",
    types: [],
  },
];

const MOCK_NOTICES = [
  "🎉 新用户首单立减 5 元",
  "📢 雨雪天气配送费上浮，请注意加价",
  "🛵 附近骑手已就绪，平均 3 分钟上门",
];

const ADDR_PLACEHOLDER = "请选择取/收货地址";

async function reverseGeocodeForLoc(
  lng: number,
  lat: number,
): Promise<{
  formatted: string;
  province: string;
  city: string;
  district: string;
} | null> {
  try {
    await loadAMap();
    const Geocoder: any = await ensureAMapPlugin("AMap.Geocoder");
    const r: any = await new Promise((resolve, reject) => {
      new Geocoder({}).getAddress([lng, lat], (s: string, res: any) => {
        if (s === "complete" && res?.info === "OK") resolve(res);
        else reject(new Error(s));
      });
    });
    const comp = r.regeocode?.addressComponent || {};
    const province = (Array.isArray(comp.province) ? "" : comp.province) || "";
    const city0 = (Array.isArray(comp.city) ? "" : comp.city) || "";
    const district = (Array.isArray(comp.district) ? "" : comp.district) || "";
    return {
      formatted: r.regeocode?.formattedAddress || "",
      province,
      city: city0,
      district,
    };
  } catch {
    return null;
  }
}

export default function RunHomePage() {
  const router = useRouter();
  const { zone, setZone, locationInfo, setLocationInfo, endLocationInfo, locationManuallySet, setLocationManuallySet, userInfo } = useAppStore();
  const [moduleIndex, setModuleIndex] = useState(0);
  const [moduleTypeIndex, setModuleTypeIndex] = useState(0);
  const [showMap, setShowMap] = useState(true);

  const currentModule = MOCK_MODULES[moduleIndex];
  const isShowStart = currentModule?.type !== "WAN";
  const isSendType = ["SEND", "1V1"].includes(currentModule?.type);

  const hasUserZone = !!zone.id;
  const hasUserAddr =
    !!locationInfo && !!locationInfo.title && locationInfo.title !== ADDR_PLACEHOLDER;

  // ---------- 首次进入：自动定位 → 仅匹配区域，不动 locationInfo ----------
  // 重要：自动定位**不**给 locationInfo 赋值，只填 zone。
  // locationInfo 完全由用户在地址簿主动选择决定（保证三个页面间流转一致）。
  useEffect(() => {
    // 已有 zone 就不重复定位
    if (zone.id) return;
    if (amapKeyMissing()) return;

    let cancelled = false;
    (async () => {
      const AMap = await loadAMap();
      const pos: any = await new Promise((resolve, reject) => {
        new AMap.Geolocation({
          enableHighAccuracy: true,
          timeout: 8000,
        }).getCurrentPosition((status: string, result: any) => {
          if (status === "complete") resolve(result);
          else reject(new Error(`定位失败: ${status}`));
        });
      });
      const lat = pos.position?.lat;
      const lng = pos.position?.lng;
      if (!lat || !lng) throw new Error("无坐标");

      // 逆地理拿城市
      let cityName = "";
      try {
        const Geocoder: any = await ensureAMapPlugin("AMap.Geocoder");
        const geo: any = await new Promise((resolve, reject) => {
          new Geocoder({}).getAddress([lng, lat], (s: string, r: any) => {
            if (s === "complete" && r?.info === "OK") resolve(r);
            else reject(new Error(s));
          });
        });
        const comp = geo.regeocode?.addressComponent || {};
        cityName = (Array.isArray(comp.city) ? "" : comp.city) || "";
      } catch {
        // 逆地理失败不影响区域匹配
      }

      if (cancelled) return;
      // 二次检查：用户可能已经手动选了 zone
      const latestZone = useAppStore.getState().zone;
      if (latestZone.id) return;

      if (cityName) {
        const shortCity = cityName.replace(/市$/, "");
        const r = await zoneList({ city: shortCity });
        if (!cancelled && r.code === 1 && r.data?.length) {
          const z = r.data[0];
          setZone({
            id: z.id,
            name: z.name,
            city: z.city,
            center_lat: z.center_lat || lat,
            center_lng: z.center_lng || lng,
          });
        }
      }
    })().catch(() => {
      // 静默
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 地图中心：优先显示起点位置（拖动改起点时跟随），无起点时用 zone 中心
  const loc = locationInfo?.location;
  const mapCenter: [number, number] =
    loc?.lat && loc?.lng
      ? [loc.lng, loc.lat]
      : zone.center_lat && zone.center_lng
        ? [zone.center_lng, zone.center_lat]
        : pickMapCenter(null, null) ?? [116.39747, 39.908823];

  // pickMode 下中心已有固定 pin，不再放起点 marker
  const handleMapClick = useCallback(
    async (lng: number, lat: number) => {
      const r = await reverseGeocodeForLoc(lng, lat);
      setLocationInfo({
        title: r?.formatted || "已选位置",
        address: r?.formatted || "",
        province: r?.province || "",
        city: r?.city || "",
        district: r?.district || "",
        location: { lat, lng },
      });
      setLocationManuallySet(true);
    },
    [setLocationInfo, setLocationManuallySet],
  );

  function gotoSubmit(type: string) {
    router.push(
      `/run/submit?module_id=${currentModule.id}&module_type=${currentModule.type}&module_type_id=${currentModule.types[moduleTypeIndex]?.id || 0}&type=${type}`,
    );
  }

  function gotoZone() {
    router.push("/zone-list");
  }

  // 起点地址：点击进入地址簿选择，选中后回到首页显示
  function gotoAddressForStart() {
    router.push(`/my/address?from=home_start`);
  }

  // 终点地址：点击进入地址簿选择，选中后直接跳下单页
  function gotoAddressForEnd() {
    router.push(`/my/address?from=home_end&module_id=${currentModule.id}&module_type=${currentModule.type}&module_type_id=${currentModule.types[moduleTypeIndex]?.id || 0}`);
  }

  return (
    <div className="page-common-bg min-h-screen pb-24">
      {/* ============ 顶部导航栏：选择区域 ============ */}
      <header
        className="bg-main text-white"
        style={{
          paddingTop: "env(safe-area-inset-top, 0px)",
        }}
      >
        <div className="flex items-center justify-center h-11 text-sm font-medium">
          <button
            onClick={gotoZone}
            className="flex items-center gap-1 px-3 py-1"
          >
            <span>{zone.name || "请选择区域"}</span>
            <ChevronDown size={14} />
          </button>
        </div>
      </header>

      {/* ============ 公告滚动条 ============ */}
      <div className="bg-white px-4 py-2 flex items-center gap-2 text-xs tn-text-regular border-b border-border">
        <Megaphone size={14} className="text-warning shrink-0" />
        <div className="flex-1 overflow-hidden">
          <div className="flex gap-6 animate-marquee whitespace-nowrap">
            {[...MOCK_NOTICES, ...MOCK_NOTICES].map((n, i) => (
              <span key={i}>{n}</span>
            ))}
          </div>
        </div>
      </div>

      {/* ============ 地图区 ============ */}
      {showMap && (
        <div className="relative h-64 overflow-hidden">
          <AMap
            center={mapCenter}
            height={256}
            zoom={14}
            fitView={false}
            pickMode
            onMapClick={handleMapClick}
          />
          <button
            onClick={() => setShowMap(false)}
            className="absolute top-2 right-2 bg-white/90 text-xs px-2 py-1 rounded-full shadow z-10"
          >
            切到轮播
          </button>
        </div>
      )}
      {!showMap && (
        <button
          onClick={() => setShowMap(true)}
          className="h-10 w-full bg-secondary/50 text-xs tn-text-secondary"
        >
          切回地图
        </button>
      )}

      {/* 提示 */}
      {showMap && (
        <div className="text-center text-xs tn-text-secondary py-1">
          拖动地图可移动起点位置
        </div>
      )}

      {/* ============ 服务卡片（Tab + 地址）============ */}
      <div className="px-4 -mt-12 relative z-10">
        <Card className="overflow-hidden">
          {/* 服务 Tab 栏 */}
          <div className="bg-secondary/50 overflow-x-auto">
            <div className="flex">
              {MOCK_MODULES.map((m, i) => (
                <button
                  key={m.id}
                  onClick={() => {
                    setModuleIndex(i);
                    setModuleTypeIndex(0);
                  }}
                  className={cn(
                    "relative px-6 py-3 text-sm whitespace-nowrap transition-all",
                    i === moduleIndex
                      ? "bg-white font-bold text-base text-text-primary"
                      : "text-text-regular",
                  )}
                >
                  <span>{m.name}</span>
                  {m.tag && (
                    <span
                      className={cn(
                        "absolute top-1 -right-1 text-[10px] px-1.5 rounded-full",
                        m.tag === "热门"
                          ? "bg-danger text-white"
                          : "bg-success text-white",
                      )}
                    >
                      {m.tag}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* 起终点地址 */}
          <div className="p-4 space-y-3">
            {isShowStart && (
              <button
                onClick={gotoAddressForStart}
                className={cn(
                  "w-full flex items-center gap-3 p-3 rounded-xl text-left",
                  isSendType
                    ? "bg-gradient-to-br from-main-light/20 to-secondary"
                    : "",
                )}
              >
                <div className="w-1 h-6 bg-text-primary rounded-full" />
                <div className="flex-1">
                  <div className="text-sm">
                    {locationInfo?.title || "请输入取件地址"}
                  </div>
                  {userInfo.mobile && (
                    <div className="text-xs tn-text-secondary mt-1 pl-1">
                      {userInfo.mobile}
                    </div>
                  )}
                </div>
              </button>
            )}

            <button
              onClick={gotoAddressForEnd}
              className={cn(
                "w-full flex items-center gap-3 p-3 rounded-xl text-left",
                !isSendType
                  ? "bg-gradient-to-br from-main-light/20 to-secondary"
                  : "",
              )}
            >
              <div className="w-1 h-6 bg-main rounded-full" />
              <div className="flex-1">
                <div className="text-sm">
                  {endLocationInfo?.title || (isSendType ? "请输入送货地址" : locationInfo?.title || "请输入收货地址")}
                </div>
                {!isSendType && userInfo.mobile && (
                  <div className="text-xs tn-text-secondary mt-1 pl-1">
                    {userInfo.mobile}
                  </div>
                )}
              </div>
            </button>
          </div>
        </Card>

        {/* 子栏目 */}
        {currentModule?.types.length > 1 && (
          <Card className="mt-3 p-4">
            <div className="grid grid-cols-4 gap-2">
              {currentModule.types.map((t, i) => (
                <button
                  key={t.id}
                  onClick={() => setModuleTypeIndex(i)}
                  className={cn(
                    "flex flex-col items-center justify-center py-3 rounded-lg transition-colors",
                    i === moduleTypeIndex
                      ? "border border-main"
                      : "hover:bg-secondary",
                  )}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={t.image}
                    alt={t.name}
                    className="w-12 h-12 object-contain"
                  />
                  <span className="text-xs mt-1 tn-text-regular">{t.name}</span>
                </button>
              ))}
            </div>
          </Card>
        )}

        {/* 服务流程 */}
        <Card className="mt-3 p-4">
          <div className="text-base font-bold tn-text-primary mb-3">
            服务流程
          </div>
          <div className="grid grid-cols-4 gap-2 text-center">
            {[
              { icon: MapPin, label: "下单" },
              { icon: Clock, label: "骑手接单" },
              { icon: "🚴", label: "取件" },
              { icon: "✅", label: "完成" },
            ].map((s, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-main-bg text-main flex items-center justify-center text-xl">
                  {typeof s.icon === "string" ? (
                    <span>{s.icon}</span>
                  ) : (
                    <s.icon size={20} />
                  )}
                </div>
                <span className="text-xs tn-text-regular mt-2">{s.label}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}